import { StoryblokImage } from '../../storyblok-types';

export type StoryblokVideoTrack = {
  _uid: string;
  kind: string;
  vtt?: {
    filename?: string;
  };
  src?: string;
  srclang?: string;
  default?: boolean;
};

export type StoryblokVideoDimension = number | string | undefined;

/**
 * MIME type of the `videoOptimized` / `mobileVideoOptimized` renditions.
 * Currently HEVC Main profile, level 5.0 (hvc1 tag — required for Safari).
 * Chromium rejects a bare `hvc1` and needs the full profile/level string;
 * Safari accepts both. When the optimized codec changes (e.g. to AV1),
 * re-upload the assets and update this one constant.
 */
export const OPTIMIZED_VIDEO_TYPE = 'video/mp4; codecs=hvc1.1.6.L150.B0';

export type StoryblokVideoItem = {
  _uid?: string;
  component?: string;
  video?: StoryblokImage;
  mobileVideo?: StoryblokImage;
  videoOptimized?: StoryblokImage;
  mobileVideoOptimized?: StoryblokImage;
  width?: StoryblokVideoDimension;
  height?: StoryblokVideoDimension;
  poster?: StoryblokImage;
  videoTrack?: StoryblokVideoTrack[];
};

const hasFilename = (asset?: StoryblokImage): boolean => !!asset?.filename;

export const getVideoItem = (videoItem: StoryblokVideoItem[] | undefined): StoryblokVideoItem | undefined => {
  return videoItem?.find(
    item =>
      hasFilename(item?.video) ||
      hasFilename(item?.mobileVideo) ||
      hasFilename(item?.videoOptimized) ||
      hasFilename(item?.mobileVideoOptimized) ||
      hasFilename(item?.poster) ||
      !!item?.videoTrack?.length ||
      item?.width !== undefined ||
      item?.height !== undefined
  );
};

export const getResponsiveVideoItemAsset = (
  item: StoryblokVideoItem | undefined,
  isMobile: boolean
): StoryblokImage | undefined => {
  const desktopAsset = hasFilename(item?.video) ? item.video : undefined;
  const mobileAsset = hasFilename(item?.mobileVideo) ? item.mobileVideo : undefined;
  return isMobile && mobileAsset ? mobileAsset : desktopAsset || mobileAsset;
};

/** Single poster used on all viewports — should be a small rendition, it only bridges until the video's first frame. */
export const getVideoItemPoster = (item: StoryblokVideoItem | undefined): StoryblokImage | undefined =>
  hasFilename(item?.poster) ? item.poster : undefined;
